import React, {useEffect, useMemo, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Card, Form, Row, Col, ButtonGroup} from 'react-bootstrap';
import {Button} from '@components';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

import {projectCreateAction} from '@app/store/reducers/project';
import {quyTrinhGetAllAction} from '@app/store/reducers/quyTrinh';
import {teamGetAllAction} from '@app/store/reducers/team';
import {URI_INTERNAL} from '@app/utils/pathLocations';
import {toast} from 'react-toastify';
import {messageToast} from '@app/utils/define';

const ProjectAdd = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const {list: quyTrinhList} = useSelector((state) => state.quyTrinh);
    const {list: teamList} = useSelector((state) => state.team);

    const [name, setName] = useState('');
    const [moTa, setMoTa] = useState('');

    const [startDate, setStartDate] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });

    const [endDate, setEndDate] = useState('');

    const [actualEndDate, setActualEndDate] = useState('');

    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);

    const toFileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

    const formatBytes = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let v = bytes;
        let i = 0;
        while (v >= 1024 && i < units.length - 1) {
            v = v / 1024;
            i += 1;
        }
        return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
    };

    const [submitting, setSubmitting] = useState(false);

    const [selectedQuyTrinhIds, setSelectedQuyTrinhIds] = useState([]);
    const [checkAllQuyTrinh, setCheckAllQuyTrinh] = useState(false);

    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [checkAllTeams, setCheckAllTeams] = useState(false);

    useEffect(() => {
        dispatch(quyTrinhGetAllAction());
        dispatch(teamGetAllAction());
    }, [dispatch]);

    const orderedQuyTrinhList = useMemo(() => {
        if (!Array.isArray(quyTrinhList)) return [];
        return [...quyTrinhList].sort(
            (a, b) => (a.thuTu || 0) - (b.thuTu || 0)
        );
    }, [quyTrinhList]);

    const orderedTeamList = useMemo(() => {
        if (!Array.isArray(teamList)) return [];
        return [...teamList].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'vi', {
                sensitivity: 'base'
            })
        );
    }, [teamList]);

    useEffect(() => {
        if (orderedQuyTrinhList.length > 0) {
            const allIds = orderedQuyTrinhList.map((x) => x.id);
            setSelectedQuyTrinhIds(allIds);
            setCheckAllQuyTrinh(true);
        }
    }, [orderedQuyTrinhList]);

    const handleToggleQuyTrinh = (id) => {
        let newIds = [];
        if (selectedQuyTrinhIds.includes(id)) {
            newIds = selectedQuyTrinhIds.filter((x) => x !== id);
        } else {
            newIds = [...selectedQuyTrinhIds, id];
        }
        setSelectedQuyTrinhIds(newIds);

        const allIds = orderedQuyTrinhList.map((x) => x.id);
        setCheckAllQuyTrinh(
            allIds.length > 0 && allIds.every((x) => newIds.includes(x))
        );
    };

    const handleToggleAllQuyTrinh = () => {
        const allIds = orderedQuyTrinhList.map((x) => x.id);
        const newValue = !checkAllQuyTrinh;
        setCheckAllQuyTrinh(newValue);
        setSelectedQuyTrinhIds(newValue ? allIds : []);
    };

    const handleToggleTeam = (id) => {
        let newIds = [];
        if (selectedTeamIds.includes(id)) {
            newIds = selectedTeamIds.filter((x) => x !== id);
        } else {
            newIds = [...selectedTeamIds, id];
        }
        setSelectedTeamIds(newIds);

        const allIds = orderedTeamList.map((x) => x.id);
        setCheckAllTeams(
            allIds.length > 0 && allIds.every((x) => newIds.includes(x))
        );
    };

    const handleToggleAllTeams = () => {
        const allIds = orderedTeamList.map((x) => x.id);
        const newValue = !checkAllTeams;
        setCheckAllTeams(newValue);
        setSelectedTeamIds(newValue ? allIds : []);
    };

    const handleFileChange = (e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;

        // Append + loại trùng (trùng theo name/size/lastModified)
        setFiles((prev) => {
            const map = new Map(prev.map((f) => [toFileKey(f), f]));
            picked.forEach((f) => map.set(toFileKey(f), f));
            return Array.from(map.values());
        });

        // Clear input để không còn hiện "n files" và cho phép chọn lại cùng file
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleClearFiles = () => {
        setFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error('Vui lòng nhập tên dự án');
            return;
        }

        if (startDate && endDate) {
            const sd = new Date(startDate);
            const ed = new Date(endDate);
            if (ed < sd) {
                toast.error('Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu');
                return;
            }
        }

        if (startDate && actualEndDate) {
            const sd = new Date(startDate);
            const ad = new Date(actualEndDate);
            if (ad < sd) {
                toast.error('Ngày hoàn thành thực tế phải >= ngày bắt đầu');
                return;
            }
        }

        const actual = actualEndDate || null;

        const model = {
            name: name.trim(),
            moTa: moTa && moTa.trim() ? moTa.trim() : null,
            startDate: startDate || null,
            endDate: endDate || null,
            actualEndDate: actual,
            quyTrinhIds: selectedQuyTrinhIds,
            teamIds: selectedTeamIds,
            files
        };

        try {
            setSubmitting(true);

            await dispatch(projectCreateAction(model)).unwrap();

            const successMsg =
                (messageToast && messageToast.messageToastCreateSuccess) ||
                'Tạo dự án thành công';

            toast.success(successMsg);
            history.push(URI_INTERNAL.PROJECT_PATH);
        } catch (err) {
            console.error('Tạo dự án lỗi:', err);

            let msg =
                (err && err.message) ||
                (typeof err === 'string' ? err : '') ||
                (err && err.title) ||
                '';

            if (!msg && err && err.errors) {
                msg = Object.values(err.errors).flat().join('; ');
            }

            const errorBaseMsg =
                (messageToast && messageToast.messageToastCreateError) ||
                'Tạo dự án thất bại';

            toast.error(msg ? `${errorBaseMsg}: ${msg}` : errorBaseMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        history.push(URI_INTERNAL.PROJECT_PATH);
    };

    return (
        <Card>
            <Card.Header as="h5">Tạo dự án mới</Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit} noValidate>
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Tên dự án <span className="text-danger">*</span>
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                type="text"
                                placeholder="Nhập tên dự án"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Ngày bắt đầu
                        </Form.Label>
                        <Col sm="3">
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Col>

                        <Form.Label className="col-sm-2 col-form-label">
                            Ngày kết thúc dự kiến
                        </Form.Label>
                        <Col sm="3">
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Ngày hoàn thành thực tế
                        </Form.Label>
                        <Col sm="4">
                            <Form.Control
                                type="date"
                                value={actualEndDate}
                                onChange={(e) =>
                                    setActualEndDate(e.target.value)
                                }
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Mô tả
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Mô tả ngắn gọn về dự án"
                                value={moTa}
                                onChange={(e) => setMoTa(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            File đính kèm
                        </Form.Label>

                        <Col sm="10">
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                disabled={submitting}
                            />

                            {files.length > 0 && (
                                <Row className="mt-2">
                                    <Col md={6}>
                                        <div className="border rounded">
                                            <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                                                <div className="text-muted">
                                                    Đã chọn {files.length} file
                                                </div>
                                                <Button
                                                    type="button"
                                                    theme="soft"
                                                    onClick={handleClearFiles}
                                                    disabled={submitting}
                                                >
                                                    Bỏ tất cả
                                                </Button>
                                            </div>

                                            <div className="px-2 pb-1">
                                                {files.map((f, idx) => (
                                                    <div
                                                        key={toFileKey(f)}
                                                        className={`d-flex justify-content-between align-items-center py-2 ${
                                                            idx < files.length - 1 ? 'border-bottom' : ''
                                                        }`}
                                                    >
                                                        <div className="me-3" style={{minWidth: 0}}>
                                                            <div className="fw-semibold text-truncate">
                                                                {f.name}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {formatBytes(f.size)}
                                                            </div>
                                                        </div>

                                                        <Button
                                                            type="button"
                                                            theme="danger"
                                                            onClick={() => handleRemoveFile(idx)}
                                                            disabled={submitting}
                                                            style={{ padding: '2px 10px', fontSize: 12, lineHeight: '18px' }}
                                                        >
                                                            Xóa
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                                Chọn bước quy trình (Phát triển phần mềm)
                            </h5>
                            <div>
                                <input
                                    type="checkbox"
                                    id="checkAllSteps"
                                    checked={checkAllQuyTrinh}
                                    onChange={handleToggleAllQuyTrinh}
                                />{' '}
                                <label htmlFor="checkAllSteps">
                                    Chọn tất cả
                                </label>
                            </div>
                        </div>
                        <Row>
                            {orderedQuyTrinhList.map((q) => (
                                <Col md={4} className="mb-2" key={q.id}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`quytrinh_${q.id}`}
                                        label={
                                            <>
                                                {q.name}{' '}
                                                {typeof q.thuTu === 'number'
                                                    ? `(Bước ${q.thuTu})`
                                                    : ''}
                                            </>
                                        }
                                        checked={selectedQuyTrinhIds.includes(
                                            q.id
                                        )}
                                        onChange={() =>
                                            handleToggleQuyTrinh(q.id)
                                        }
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                                Chọn nhóm thực hiện dự án
                            </h5>
                            <div>
                                <input
                                    type="checkbox"
                                    id="checkAllTeams"
                                    checked={checkAllTeams}
                                    onChange={handleToggleAllTeams}
                                />{' '}
                                <label htmlFor="checkAllTeams">
                                    Chọn tất cả
                                </label>
                            </div>
                        </div>
                        <Row>
                            {orderedTeamList.map((t) => (
                                <Col md={4} className="mb-2" key={t.id}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`team_${t.id}`}
                                        label={t.name}
                                        checked={selectedTeamIds.includes(t.id)}
                                        onChange={() => handleToggleTeam(t.id)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Col md={{span: 10, offset: 2}}>
                            <ButtonGroup className="mb-2">
                                <Button
                                    type="submit"
                                    theme="primary"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Đang lưu...' : 'Tạo dự án'}
                                </Button>
                                &nbsp;&nbsp;&nbsp;
                                <Button
                                    type="button"
                                    theme="secondary"
                                    onClick={handleCancel}
                                    disabled={submitting}
                                >
                                    Hủy
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Form.Group>
                </Form>

                <ReactSpinner loadings={submitting} />
            </Card.Body>
        </Card>
    );
};

export default ProjectAdd;
